import { useRef, useState } from 'react';
import { cn } from '@/lib/cn';

const STEP = 2;
const LABEL_FADE_THRESHOLD = 15;

export interface BeforeAfterSliderProps {
  beforeSrc: string;
  afterSrc: string;
  beforeLabel: string;
  afterLabel: string;
  beforeAlt: string;
  afterAlt: string;
  ariaLabel: string;
  width: number;
  height: number;
  className?: string;
}

// §34.4 - reusable before/after image comparison slider. Pointer Events only
// (no separate mouse/touch handlers) so mouse and touch share one code path.
// The handle captures the pointer on pointerdown so a touch/mouse drag keeps
// tracking it even as the pointer moves elsewhere; the container only
// handles pointerdown-to-jump. No network calls, no persisted state.
export function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  beforeLabel,
  afterLabel,
  beforeAlt,
  afterAlt,
  ariaLabel,
  width,
  height,
  className,
}: BeforeAfterSliderProps) {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);

  function positionFromClientX(clientX: number): number {
    const rect = containerRef.current!.getBoundingClientRect();
    const ratio = ((clientX - rect.left) / rect.width) * 100;
    return Math.min(100, Math.max(0, ratio));
  }

  function handleContainerPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.target === handleRef.current || handleRef.current?.contains(e.target as Node)) return;
    setPosition(positionFromClientX(e.clientX));
  }

  function handleHandlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.stopPropagation();
    handleRef.current?.setPointerCapture(e.pointerId);
    setPosition(positionFromClientX(e.clientX));
  }

  function handleHandlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!handleRef.current?.hasPointerCapture(e.pointerId)) return;
    setPosition(positionFromClientX(e.clientX));
  }

  function handleHandlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (handleRef.current?.hasPointerCapture(e.pointerId)) {
      handleRef.current.releasePointerCapture(e.pointerId);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setPosition((p) => Math.max(0, p - STEP));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setPosition((p) => Math.min(100, p + STEP));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setPosition(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setPosition(100);
    }
  }

  const roundedPosition = Math.round(position);
  const beforeLabelOpacity = position < LABEL_FADE_THRESHOLD ? 0 : 1;
  const afterLabelOpacity = position > 100 - LABEL_FADE_THRESHOLD ? 0 : 1;

  return (
    <div
      ref={containerRef}
      onPointerDown={handleContainerPointerDown}
      className={cn('relative w-full select-none overflow-hidden', className)}
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      <img
        src={beforeSrc}
        alt={beforeAlt}
        loading="lazy"
        width={width}
        height={height}
        className="absolute inset-0 h-full w-full object-cover"
        draggable={false}
      />
      <img
        src={afterSrc}
        alt={afterAlt}
        loading="lazy"
        width={width}
        height={height}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 0 0 ${position}%)` }}
        draggable={false}
      />

      <div
        className="pointer-events-none absolute top-3 left-3 rounded-[var(--radius-input)] bg-black/60 px-2.5 py-1 text-xs font-semibold text-white transition-opacity"
        style={{ opacity: beforeLabelOpacity }}
      >
        {beforeLabel}
      </div>
      <div
        className="pointer-events-none absolute top-3 right-3 rounded-[var(--radius-input)] bg-black/60 px-2.5 py-1 text-xs font-semibold text-white transition-opacity"
        style={{ opacity: afterLabelOpacity }}
      >
        {afterLabel}
      </div>

      <div className="pointer-events-none absolute inset-y-0 w-0.5 bg-white" style={{ left: `${position}%` }} />

      <div
        ref={handleRef}
        role="slider"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={roundedPosition}
        onPointerDown={handleHandlePointerDown}
        onPointerMove={handleHandlePointerMove}
        onPointerUp={handleHandlePointerUp}
        onKeyDown={handleKeyDown}
        className="absolute top-1/2 grid h-9 w-9 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize place-items-center rounded-full border-2 border-white bg-accent text-on-accent shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        style={{ left: `${position}%`, touchAction: 'none' }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M5 2L1 7l4 5M9 2l4 5-4 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}
